import { BuildingOfficeIcon } from "@heroicons/react/24/outline";
import { CommunityOrgTypeSentinel, PromptId, ReferralTypesOptions } from "@sp-crm/core";
import { SearchbarItemBadge } from "components/community-search/search-bar/searchbar-item-badge";
import { Checkbox } from "components/ui/checkbox";
import { defaultLinkStyle } from "components/ui/link";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { SearchInput } from "components/ui/search-input";
import React, { useCallback, useMemo, useState } from "react";
import { useCommunityTypeQuestion, useRegionId, useRegions } from "store/selectors/hooks";

interface OrgTypeFilterProps {
    value: string[];
    onChange: (orgTypes: string[]) => void;
    communityTypes: PromptId[];
    onCommunityTypesChange: (communityTypes: PromptId[]) => void;
    onClear: () => void;
}

export const OrgTypeFilter: React.FC<OrgTypeFilterProps> = props => {
    const { value, onChange, communityTypes, onCommunityTypesChange, onClear } = props;

    const [showPanel, setShowPanel] = useState(false);
    const [itemFilterText, setItemFilterText] = useState("");

    const regionId = useRegionId();
    const regions = useRegions();
    const region = regions.find(r => r.id === regionId);
    const regionKey = region?.key;
    const communityTypeQuestion = useCommunityTypeQuestion(regionKey, regionId);

    const addOrgType = (orgType: string) => {
        if (!value.includes(orgType)) {
            const newOrgTypes = [...value, orgType];
            onChange(newOrgTypes);
        }
    };

    const removeOrgType = (orgType: string) => {
        if (value.includes(orgType)) {
            const newOrgTypes = value.filter(ot => ot !== orgType);
            onChange(newOrgTypes);
        }
    };

    const addCommunityType = (communityType: PromptId) => {
        if (!communityTypes.includes(communityType)) {
            const newCommunityTypes = [...communityTypes, communityType];
            onCommunityTypesChange(newCommunityTypes);
        }
    };

    const removeCommunityType = (communityType: PromptId) => {
        if (communityTypes.includes(communityType)) {
            const newCommunityTypes = communityTypes.filter(ct => ct !== communityType);
            onCommunityTypesChange(newCommunityTypes);
        }
    };

    const clearAll = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            onClear();
        },
        [onClear],
    );

    const headerGroups = useMemo(() => {
        return [...ReferralTypesOptions]
            .map(o => {
                return {
                    header: o.header,
                    groupOptions: o.groupOptions.filter(
                        g =>
                            o.header
                                .toLowerCase()
                                .includes(itemFilterText.toLowerCase()) ||
                            g.text.toLowerCase().includes(itemFilterText.toLowerCase()),
                    ),
                };
            })
            .filter(o => o.groupOptions.length > 0);
    }, [itemFilterText]);

    const showRecordTypeOption = useMemo(() => {
        return (
            "Record Type".toLowerCase().includes(itemFilterText.toLowerCase()) ||
            "Community".toLowerCase().includes(itemFilterText.toLowerCase())
        );
    }, [itemFilterText]);

    const filteredPrompts = useMemo(() => {
        if (!communityTypeQuestion) return [];
        return communityTypeQuestion.prompts.filter(
            p =>
                communityTypeQuestion.title
                    .toLowerCase()
                    .includes(itemFilterText.toLowerCase()) ||
                p.title.toLowerCase().includes(itemFilterText.toLowerCase()),
        );
    }, [communityTypeQuestion, itemFilterText]);

    return (
        <>
            <div
                className="flex items-center space-x-1 cursor-pointer text-gray-700 lg:whitespace-nowrap"
                onClick={() => setShowPanel(true)}>
                <BuildingOfficeIcon className="w-5 h-5 flex-shrink-0 mr-2" />
                <span className="inline md:inline text-sm">Organization type</span>
                {value.length + communityTypes.length > 0 ? (
                    <SearchbarItemBadge counter={value.length + communityTypes.length} />
                ) : null}
            </div>
            <Panel
                headerText="Organization type"
                type={PanelType.medium}
                isOpen={showPanel}
                onDismiss={() => {
                    setShowPanel(false);
                    setItemFilterText("");
                }}>
                <div className="space-y-4">
                    <SearchInput
                        value={itemFilterText}
                        onChange={e => {
                            setItemFilterText(e.target.value);
                        }}
                        onClear={() => setItemFilterText("")}
                    />
                    <a
                        href="#"
                        className={`${defaultLinkStyle} block`}
                        onClick={clearAll}>
                        Clear all
                    </a>
                    <ul>
                        {headerGroups.map((option, index) => (
                            <li key={index}>
                                <p className="font-bold">{option.header}</p>
                                <ul>
                                    {option.groupOptions.map(
                                        (groupOption, groupOptionIndex) => (
                                            <li key={groupOptionIndex}>
                                                <Checkbox
                                                    checked={value.includes(
                                                        groupOption.value,
                                                    )}
                                                    onChange={e => {
                                                        e.target.checked
                                                            ? addOrgType(
                                                                  groupOption.value,
                                                              )
                                                            : removeOrgType(
                                                                  groupOption.value,
                                                              );
                                                    }}
                                                    label={groupOption.text}
                                                />
                                            </li>
                                        ),
                                    )}
                                </ul>
                            </li>
                        ))}
                        {communityTypeQuestion &&
                            filteredPrompts &&
                            filteredPrompts.length > 0 && (
                                <li>
                                    <p className="font-bold">
                                        {communityTypeQuestion.title}
                                    </p>
                                    <ul>
                                        {filteredPrompts.map(prompt => (
                                            <li key={prompt.id}>
                                                <Checkbox
                                                    checked={communityTypes.includes(
                                                        prompt.id,
                                                    )}
                                                    onChange={e => {
                                                        e.target.checked
                                                            ? addCommunityType(prompt.id)
                                                            : removeCommunityType(
                                                                  prompt.id,
                                                              );
                                                    }}
                                                    label={prompt.title}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            )}
                        {showRecordTypeOption && (
                            <li>
                                <p className="font-bold">Record Type</p>
                                <ul>
                                    <li>
                                        <Checkbox
                                            checked={value.includes(
                                                CommunityOrgTypeSentinel,
                                            )}
                                            onChange={e => {
                                                e.target.checked
                                                    ? addOrgType(CommunityOrgTypeSentinel)
                                                    : removeOrgType(
                                                          CommunityOrgTypeSentinel,
                                                      );
                                            }}
                                            label="Include All Communities"
                                        />
                                    </li>
                                </ul>
                            </li>
                        )}
                    </ul>
                </div>
            </Panel>
        </>
    );
};
